import { useState, useEffect, useMemo } from 'react';

interface Section {
  level: number;
  title: string;
  startWordIndex: number;
  endWordIndex: number;
}

export const useSectionTracking = (content: string, currentWordIndex: number) => {
  const [currentSection, setCurrentSection] = useState<Section | null>(null);
  const [sectionProgress, setSectionProgress] = useState(0);

  // Parse sections from markdown content
  const sections = useMemo(() => {
    const sectionList: Section[] = [];
    const lines = content.split('\n');
    let wordCounter = 0;

    lines.forEach((line, lineIndex) => {
      const h1Match = line.match(/^#\s+(.+)/);
      const h2Match = line.match(/^##\s+(.+)/);
      const h3Match = line.match(/^###\s+(.+)/);

      if (h1Match || h2Match || h3Match) {
        // Close previous section
        if (sectionList.length > 0) {
          sectionList[sectionList.length - 1].endWordIndex = wordCounter - 1;
        }

        // Create new section
        const level = h1Match ? 1 : h2Match ? 2 : 3;
        const title = (h1Match || h2Match || h3Match)![1];
        
        sectionList.push({
          level,
          title,
          startWordIndex: wordCounter,
          endWordIndex: 0, // Will be set when next section starts or at end
        });
      }

      // Count words in this line
      const words = line.split(/\s+/).filter(word => word.trim().length > 0);
      wordCounter += words.length;
    });

    // Close last section
    if (sectionList.length > 0) {
      sectionList[sectionList.length - 1].endWordIndex = wordCounter;
    }

    return sectionList;
  }, [content]);

  // Track current section and progress
  useEffect(() => {
    if (sections.length === 0) {
      setCurrentSection(null);
      setSectionProgress(0);
      return;
    }

    // Find section containing current word index
    const activeSection = sections.find(
      section =>
        currentWordIndex >= section.startWordIndex &&
        currentWordIndex <= section.endWordIndex
    );

    if (activeSection) {
      setCurrentSection(activeSection);

      // Calculate progress within section
      const sectionLength = activeSection.endWordIndex - activeSection.startWordIndex;
      const wordsIntoSection = currentWordIndex - activeSection.startWordIndex;
      const progress = sectionLength > 0 ? (wordsIntoSection / sectionLength) * 100 : 0;
      
      setSectionProgress(Math.min(Math.max(progress, 0), 100));
    } else {
      // If no section found, might be in intro before first section
      setCurrentSection(sections[0]);
      setSectionProgress(0);
    }
  }, [currentWordIndex, sections]);

  // Helper function to get time position of a section
  const getSectionTimePosition = (
    section: Section,
    totalWords: number,
    duration: number
  ): number => {
    if (totalWords === 0 || duration === 0) return 0;
    const wordRatio = section.startWordIndex / totalWords;
    return wordRatio * duration;
  };

  // Calculate total words in content
  const totalWords = useMemo(() => {
    const words = content.split(/\s+/).filter(word => word.trim().length > 0);
    return words.length;
  }, [content]);

  return {
    sections,
    currentSection,
    sectionProgress,
    getSectionTimePosition,
    totalWords,
  };
};
